import {
  Injectable, OnDestroy,
} from '@angular/core';
import {BrowserService} from 'webcommon/legacy-common';
import { FoxitStyleService } from 'webcommon/lazy-load-commonjs/foxit';
import { ApDeferred } from 'webcommon/shared';

@Injectable({
  providedIn: 'root',
})
export class StyleService implements FoxitStyleService, OnDestroy {
  private readonly documentObj: Document;
  private processById = new Map<string, Promise<void>>();

  constructor(
    browserService: BrowserService,
  ) {
    this.documentObj = browserService.getDocument();
  }

  ngOnDestroy(): void {
    this.processById.forEach((_value, key, map) => {
      map.delete(key);
    });
  }

  // The current use-case for this method is to add a style that is lazy-loaded with dynamic import(),
  // that only needs to get added to the DOM once.
  // replaceCurrent:
  // true => if a style exists with this id,
  // replace the current style content with this content
  // false => if a style exists with this id, do nothing
  async addStyleToDom(
    id: string,
    content: () => (Promise<string> | string),
    replaceCurrent = false,
  ): Promise<void> {
    const fullId = `ap-lazystyle-${id}`;
    let element = this.documentObj.getElementById(fullId);
    if (element) {
      if (replaceCurrent) {
        element.textContent = await content();
      }
      return;
    }

    element = this.documentObj.createElement('style');
    element.id = fullId;
    element.textContent = await content();
    this.documentObj.head.appendChild(element);
  }

  // The current use-case for this method is to add a style that is lazy-loaded via assets (inject: false)
  // that only needs to get added to the DOM once.
  // If there is already a link element with this file, then it won't get added again.
  async addStyleUrlToDom(
    id: string,
    stylesheetUrl: Promise<string> | string
  ): Promise<void> {
    const fullId = `ap-lazystyle-url-${id}`;
    if (this.documentObj.getElementById(fullId)) {
      console.log(`Skipping adding StyleUrl for ${fullId} to avoid a duplicate css file.`);
      return;
    }

    // if it's already in the process of being added, wait on that promise, instead of adding again
    const existingPromise = this.processById.get(fullId);
    if (existingPromise) {
      console.log(`Skipping adding StyleUrl for ${fullId} to avoid processing a duplicate css file.`);
      return await existingPromise;
    }

    const deferred = new ApDeferred<void>();
    this.processById.set(fullId, deferred.promise);
    this.forceAddStyleUrlToDom(fullId, stylesheetUrl).then(
      x => deferred.resolve(x),
      err => deferred.reject(err),
    );
    return deferred.promise;
  }

  private async forceAddStyleUrlToDom(
    fullId: string,
    stylesheetUrl: Promise<string> | string
  ): Promise<void> {
    const _stylesheetUrl = await stylesheetUrl;
    return new Promise<void>((resolve, reject) => {
      const link = this.documentObj.createElement('link');
      link.onload = () => {
        resolve();
      };
      link.onerror = (e) => {
        reject(e);
      };
      link.id = fullId;
      link.rel = 'stylesheet';
      link.href = _stylesheetUrl;
      this.documentObj.head.appendChild(link);
    });
  }
}
